import { useCallback, useEffect, useRef, useState } from 'react'
import { isAbortError } from '../core/api/http'
import type { RequestOptions } from '../core/types/backend'

export type LoadStatus = 'loading' | 'ready' | 'error'

/**
 * Laadt data en houdt alleen het antwoord op de láátste aanvraag over: snel wisselen
 * (bijv. tussen klanten) mag nooit een ouder antwoord over een nieuwer heen zetten.
 *
 * Een oudere aanvraag wordt ook echt afgebroken (AbortController), niet alleen genegeerd:
 * geen verspilde verbindingen bij snel doorklikken, en niets meer onderweg na wegnavigeren.
 * `load` moet stabiel zijn (useCallback of een module-functie); verandert hij, dan wordt opnieuw geladen.
 */
export function useLatestLoad<T>(load: (opts: RequestOptions) => Promise<T>) {
  const [data, setData] = useState<T | null>(null)
  const [status, setStatus] = useState<LoadStatus>('loading')
  const current = useRef<AbortController | null>(null)

  const fetchLatest = useCallback((quiet: boolean) => {
    current.current?.abort()
    const controller = new AbortController()
    current.current = controller
    if (!quiet) setStatus('loading')
    load({ signal: controller.signal })
      .then((value) => {
        if (controller.signal.aborted) return
        setData(value)
        setStatus('ready')
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || isAbortError(err) || quiet) return
        setStatus('error')
      })
  }, [load])

  /** Opnieuw laden met laadstatus (ook als knop te gebruiken: `onClick={reload}`). */
  const reload = useCallback(() => fetchLatest(false), [fetchLatest])
  /** Op de achtergrond verversen: het scherm blijft staan, een fout laat de huidige data staan. */
  const refresh = useCallback(() => fetchLatest(true), [fetchLatest])

  useEffect(() => {
    reload()
    return () => current.current?.abort()
  }, [reload])

  return { data, setData, status, reload, refresh }
}
